import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { QuestionHistoryRowSkeleton } from './QuestionHistoryRowSkeleton';

import { Button, ChangeIndicator, Tag, Tooltip } from '@/components/atoms';
import { AllQuestionItem } from '@/types/questions';
import { formatEnpsScoreForDisplay, isEnpsCategoryId } from '@/utils/enpsDisplay';
import { formatRowDate } from '@/utils/formatRowDate';
import { hasQuestionHistoryScore } from '@/utils/questionHistoryDisplay';

import { formatNumberWithComma } from '../../../../utils/numberFormatter';
import { TableCell, TableRow } from '../Table/Table';

type QuestionHistoryRowProps = {
  rowData: AllQuestionItem;
  onClick: () => void;
  className?: string;
  isLoading?: boolean;
  /** When true, hide the comparison delta (vs company average/baseline). */
  hideScoreComparison?: boolean;
};

export const QuestionHistoryRow: FC<QuestionHistoryRowProps> = ({
  rowData,
  onClick,
  className,
  isLoading,
  hideScoreComparison,
}) => {
  const { t } = useTranslation('components');
  if (isLoading) {
    return <QuestionHistoryRowSkeleton />;
  }

  const { date, questionText, categoryName, categoryId, score, comparedToAverage, responseCount } =
    rowData;

  const { month, day } = formatRowDate(date);

  const formattedResponses = formatNumberWithComma(responseCount);
  const isEnpsRow = isEnpsCategoryId(categoryId);
  const hasScore = hasQuestionHistoryScore(score);
  const scoreDisplay = hasScore
    ? isEnpsRow
      ? formatEnpsScoreForDisplay(score)
      : `${score}%`
    : null;
  const showComparison =
    hasScore &&
    !hideScoreComparison &&
    comparedToAverage != null &&
    Number.isFinite(comparedToAverage);

  return (
    <TableRow className={className}>
      <TableCell className="flex-col gap-1 items-start min-w-[120px]">
        <p>{month}</p>
        <p className="text-body-12-regular text-icons-secondary">{day}</p>
      </TableCell>
      <TableCell className="text-body-14-semibold">{questionText}</TableCell>
      <TableCell className="min-w-[100px] max-w-[145px]">
        <Tooltip title={categoryName}>
          <Tag
            label={categoryName}
            size="regular"
            color="cyan"
            className="inline-flex max-w-full overflow-hidden"
            labelClassName="truncate"
          />
        </Tooltip>
      </TableCell>
      <TableCell>
        {hasScore ? (
          <div className="flex items-center gap-1">
            <span className="text-body-14-semibold">{scoreDisplay}</span>
            {showComparison && (
              <ChangeIndicator
                changeValue={comparedToAverage}
                valueUnit={isEnpsRow ? 'points' : 'percent'}
              />
            )}
          </div>
        ) : (
          <span className="text-body-14-regular text-custom-warm-grey-400">
            {t('questionHistoryRow.insufficientResponses')}
          </span>
        )}
      </TableCell>
      <TableCell className="min-w-[200px]">
        <span className="text-subtitle-secondary">
          {t('questionHistoryRow.responseCount', { count: responseCount, formattedResponses })}
        </span>
      </TableCell>
      <TableCell>
        <Button onClick={onClick} size="md" variant="secondary">
          {t('questionHistoryRow.questionButton')}
        </Button>
      </TableCell>
    </TableRow>
  );
};
